import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MEAL_SLOTS, kcalFromMacros, kcalLooksWrong } from '@kraftwerk/shared';
import type { MealSlot } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalRecipe } from '../../db/local.js';
import { createRecipe, updateRecipe } from '../../db/nutrition.js';
import { MacroField, toNumber } from '../nutrition/FoodForm.js';
import { NutrientFields, nutrientInput, nutrientsFromInput } from '../nutrition/NutrientFields.js';

export function RecipeForm({
  recipe,
  onDone,
  onCancel,
}: {
  recipe?: LocalRecipe;
  /** Mit der Kennung des gespeicherten Gerichts — beim Anlegen die neue. */
  onDone: (recipeId: string) => void;
  onCancel?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const ids = {
    name: useId(),
    slot: useId(),
    portions: useId(),
    items: useId(),
    prepNote: useId(),
    note: useId(),
    sourceUrl: useId(),
  };

  const [name, setName] = useState(recipe?.name ?? '');
  const [slot, setSlot] = useState<MealSlot>(recipe?.slot ?? 'lunch');
  const [portions, setPortions] = useState(String(recipe?.portions ?? 1));
  const [kcal, setKcal] = useState(recipe?.kcalPerPortion?.toString() ?? '');
  const [protein, setProtein] = useState(recipe?.proteinPerPortion?.toString() ?? '');
  const [carbs, setCarbs] = useState(recipe?.carbsPerPortion?.toString() ?? '');
  const [fat, setFat] = useState(recipe?.fatPerPortion?.toString() ?? '');
  const [nutrients, setNutrients] = useState(nutrientInput(recipe?.nutrients));
  const [items, setItems] = useState(recipe?.items ?? '');
  const [prepNote, setPrepNote] = useState(recipe?.prepNote ?? '');
  const [note, setNote] = useState(recipe?.note ?? '');
  const [sourceUrl, setSourceUrl] = useState(recipe?.sourceUrl ?? '');

  const entered = {
    kcal: toNumber(kcal) ?? 0,
    protein: toNumber(protein) ?? 0,
    carbs: toNumber(carbs) ?? 0,
    fat: toNumber(fat) ?? 0,
  };
  const warnKcal = kcalLooksWrong(entered);

  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-accent bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() === '') return;
        const draft = {
          name,
          slot,
          portions: toNumber(portions) ?? 1,
          kcalPerPortion: toNumber(kcal),
          proteinPerPortion: toNumber(protein),
          carbsPerPortion: toNumber(carbs),
          fatPerPortion: toNumber(fat),
          nutrients: nutrientsFromInput(nutrients),
          items: items.trim() || null,
          prepNote: prepNote.trim() || null,
          note: note.trim() || null,
          // Nur Webadressen — der Server nimmt ohnehin nichts anderes an.
          sourceUrl: /^https?:\/\/\S+$/i.test(sourceUrl.trim()) ? sourceUrl.trim() : null,
        };
        void (
          recipe
            ? updateRecipe(recipe.id, draft, db).then(() => recipe.id)
            : createRecipe(draft, db).then((row) => row.id)
        ).then(onDone);
      }}
    >
      <div className="flex flex-wrap gap-2">
        <label
          htmlFor={ids.name}
          className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-ink-muted"
        >
          {t('nutrition.recipe.name')}
          <input
            id={ids.name}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            maxLength={120}
            className="kw-field text-ink"
          />
        </label>

        <label htmlFor={ids.slot} className="flex flex-col gap-1 text-xs text-ink-muted">
          {t('nutrition.recipe.slot')}
          <select
            id={ids.slot}
            value={slot}
            onChange={(event) => setSlot(event.target.value as MealSlot)}
            className="kw-field text-ink"
          >
            {MEAL_SLOTS.map((option) => (
              <option key={option} value={option}>
                {t(`nutrition.slot.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <label htmlFor={ids.portions} className="flex w-24 flex-col gap-1 text-xs text-ink-muted">
          {t('nutrition.recipe.portions')}
          <input
            id={ids.portions}
            inputMode="decimal"
            value={portions}
            onChange={(event) => setPortions(event.target.value)}
            className="kw-field text-ink"
          />
        </label>
      </div>

      {recipe?.macroSource === 'ingredients' ? (
        <p className="text-xs text-ink-muted">{t('nutrition.recipe.ingredientsWin')}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <MacroField label={t('nutrition.macro.kcal')} value={kcal} onChange={setKcal} />
          <MacroField label={t('nutrition.macro.protein')} value={protein} onChange={setProtein} />
          <MacroField label={t('nutrition.macro.carbs')} value={carbs} onChange={setCarbs} />
          <MacroField label={t('nutrition.macro.fat')} value={fat} onChange={setFat} />
          <div className="w-full">
            <NutrientFields
              value={nutrients}
              onChange={setNutrients}
              basis={t('nutrients.perPortion')}
            />
          </div>
        </div>
      )}

      {warnKcal ? (
        <p role="alert" className="text-xs text-warn">
          {t('nutrition.recipe.kcalMismatch', { computed: Math.round(kcalFromMacros(entered)) })}
        </p>
      ) : null}

      <label htmlFor={ids.items} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('nutrition.recipe.items')}
        <textarea
          id={ids.items}
          value={items}
          onChange={(event) => setItems(event.target.value)}
          rows={2}
          className="kw-field text-ink"
        />
      </label>

      <label htmlFor={ids.prepNote} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('nutrition.recipe.prepNote')}
        <input
          id={ids.prepNote}
          value={prepNote}
          onChange={(event) => setPrepNote(event.target.value)}
          maxLength={300}
          placeholder={t('nutrition.recipe.prepNoteHint')}
          className="kw-field text-ink"
        />
      </label>

      <label htmlFor={ids.note} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('dish.note')}
        <textarea
          id={ids.note}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          maxLength={2000}
          className="kw-field text-ink"
        />
      </label>

      <label htmlFor={ids.sourceUrl} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('dish.sourceUrl')}
        <input
          id={ids.sourceUrl}
          type="url"
          inputMode="url"
          value={sourceUrl}
          onChange={(event) => setSourceUrl(event.target.value)}
          placeholder="https://"
          className="kw-field text-ink"
        />
      </label>

      <div className="flex gap-2">
        <button type="submit" className="kw-btn kw-btn-small">
          {t('nutrition.library.save')}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-line px-3 py-2 text-sm text-ink-muted hover:border-accent"
          >
            {t('nutrition.library.cancel')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
